import { useCallback, useEffect, useState } from "react";
import { UserPlus, ShieldCheck, Inbox, KeyRound, Trash2, Check, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AccessEditor } from "@/components/AccessEditor";
import { AdminSecurityCard } from "@/components/AdminSecurityCard";
import { UserCapsCard, SuperAdminHandoverCard } from "@/components/PlatformCards";
import { LabsCard } from "@/components/LabsCard";
import { PluginsCard, LoginActivityCard } from "@/components/AdminExtras";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const Section = ({ title, desc, icon: Icon, children, testid }) => (
  <div className="rounded-md border border-[#E5E7EB] bg-white p-5" data-testid={testid}>
    <div className="flex items-start gap-3">
      <Icon className="h-5 w-5 text-[#002FA7] mt-0.5" />
      <div>
        <h2 className="font-heading font-bold text-[#111827]">{title}</h2>
        <p className="text-xs text-[#6B7280] mt-0.5">{desc}</p>
      </div>
    </div>
    <div className="mt-5">{children}</div>
  </div>
);

export default function AdminPanel() {
  const { user, checkAuth } = useAuth();
  const isPrimary = !!user?.is_primary_admin;
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", role: "technician", packs: ["testing"] });
  const [packMeta, setPackMeta] = useState([]);
  const [twofa, setTwofa] = useState(null);
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(null);
  const [showDecided, setShowDecided] = useState(false);
  const [accessTarget, setAccessTarget] = useState(null);
  const [labs, setLabs] = useState([]);
  const twofaOn = !!user?.totp_enabled;

  const load = useCallback(async () => {
    if (!twofaOn) return;
    try {
      const [b, u] = await Promise.all([api.get("/beta-requests"), api.get("/users")]);
      setRequests(b.data); setUsers(u.data);
      const { data } = await api.get("/access/packs");
      setPackMeta(data.packs || []);
      const l = await api.get("/labs");
      setLabs(l.data || []);
    } catch (e) { toast.error(errMsg(e)); }
  }, [twofaOn]);

  const setLab = async (u, labId) => {
    try {
      await api.put(`/admin/users/${u.user_id}/lab`, { lab_id: labId || null });
      toast.success(labId ? `${u.email} linked to the selected lab` : `${u.email} de-linked`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => { load(); }, [load]);

  const approve = async (req) => {
    const role = window.prompt(`Approve ${req.full_name} as which role? (technician / pathologist${isPrimary ? " / admin" : ""})`, "technician");
    if (!role) return;
    try {
      const { data } = await api.post(`/beta-requests/${req.request_id}/approve`, { role: role.trim() });
      toast.success(data.temp_password
        ? `Approved. Temporary password for ${data.email}: ${data.temp_password}`
        : `Approved and activated ${data.email}`, { duration: 20000 });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const reject = async (req) => {
    const reason = window.prompt(`Reason for rejecting ${req.full_name}?`, "");
    if (reason === null) return;
    try {
      await api.post(`/beta-requests/${req.request_id}/reject`, { reason });
      toast.success("Request rejected");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/users", form);
      toast.success(`Created ${data.email}. Temporary password: ${data.temp_password}`, { duration: 25000 });
      setForm({ name: "", email: "", role: "technician", packs: ["testing"] });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const setStatus = async (u, status) => {
    try {
      await api.put(`/admin/users/${u.user_id}/status`, { status });
      toast.success(`${u.email} is now ${status}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setRole = async (u, role) => {
    try {
      await api.put(`/users/${u.user_id}/role`, { role });
      toast.success(`${u.email} is now ${role}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const resetPassword = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/reset-password`);
      toast.success(`Temporary password for ${u.email}: ${data.temp_password}`, { duration: 25000 });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.user_id}`);
      toast.success("User removed");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const start2fa = async () => {
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setTwofa(data);
      setBackup(null);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const enable2fa = async () => {
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      setBackup(data.backup_codes);
      setTwofa(null);
      setCode("");
      toast.success("Two-factor authentication enabled");
      await checkAuth();
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const disable2fa = async () => {
    const c = window.prompt("Enter the current 6-digit code to turn 2FA off");
    if (!c) return;
    try {
      await api.post("/auth/2fa/disable", { code: c });
      toast.success("Two-factor authentication disabled — the Admin Panel stays locked until you re-enable it");
      await checkAuth();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const pending = requests.filter((r) => r.status === "pending");
  const visible = showDecided ? requests : pending;

  const TwoFactorCard = (
    <Section title="Two-Factor Authentication" desc="Mandatory for every administrator, including the primary admin."
      icon={ShieldCheck} testid="twofa-card">
      {!twofa && !backup && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={start2fa} data-testid="twofa-setup-btn" className="bg-[#002FA7] hover:bg-[#00237C] text-white">
            {twofaOn ? "Re-run 2FA setup" : "Set up 2FA"}
          </Button>
          {twofaOn && <Button variant="outline" onClick={disable2fa} data-testid="twofa-disable-btn">Turn 2FA off</Button>}
        </div>
      )}
      {twofa && (
        <div data-testid="twofa-setup-panel">
          <img src={`data:image/png;base64,${twofa.qr_png_base64}`} alt="2FA QR code"
            className="h-40 w-40 border border-[#E5E7EB] rounded-md" data-testid="twofa-qr" />
          <p className="text-xs text-[#6B7280] mt-3">Scan with Google Authenticator / Authy, or enter this key manually:</p>
          <code className="block text-xs bg-[#F3F4F6] rounded p-2 mt-1 break-all">{twofa.secret}</code>
          <div className="flex gap-2 mt-3">
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code"
              data-testid="twofa-code-input" className="w-40" />
            <Button onClick={enable2fa} data-testid="twofa-enable-btn" className="bg-[#002FA7] hover:bg-[#00237C] text-white">
              Enable
            </Button>
          </div>
        </div>
      )}
      {backup && (
        <div data-testid="twofa-backup-codes">
          <p className="text-sm text-[#111827]">Save these one-time backup codes somewhere safe:</p>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {backup.map((c) => (<code key={c} className="text-xs bg-[#F3F4F6] rounded p-2 text-center">{c}</code>))}
          </div>
        </div>
      )}
    </Section>
  );

  if (!twofaOn) {
    return (
      <div data-testid="twofa-gate">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#111827]">Admin Panel locked</h1>
        <p className="text-sm text-[#6B7280] mt-1 max-w-2xl">
          Two-factor authentication is mandatory for administrators. Set it up below to unlock beta requests,
          user provisioning, feature access, login activity and plugins.
        </p>
        <div className="max-w-xl mt-6">{TwoFactorCard}</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-panel-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#111827]">Admin Panel</h1>
      <p className="text-sm text-[#6B7280] mt-1">
        Restricted area — beta applications, user provisioning and account security.
        {isPrimary ? " You are the primary administrator." : " Only the primary administrator can manage other administrators."}
      </p>

      <div className="grid xl:grid-cols-2 gap-4 mt-6 [&>*]:min-w-0">
        <div className="xl:col-span-2"><AdminSecurityCard /></div>
        {isPrimary && <div className="xl:col-span-2"><LabsCard onChanged={load} /></div>}
        {isPrimary && <UserCapsCard />}
        {isPrimary && <SuperAdminHandoverCard users={users} onDone={load} />}
        <Section title={`Beta Requests (${pending.length} pending)`} desc="Approve to create an account instantly, or reject with a reason."
          icon={Inbox} testid="beta-requests-card">
          <button onClick={() => setShowDecided(!showDecided)} data-testid="toggle-decided-btn"
            className="text-xs text-[#002FA7] hover:underline mb-3">
            {showDecided ? "Show pending only" : `Show decided requests (${requests.length - pending.length})`}
          </button>
          <div className="space-y-3 max-h-[520px] overflow-y-auto">
            {visible.length === 0 && <p className="text-sm text-[#6B7280]">No applications to review.</p>}
            {visible.map((rq) => (
              <div key={rq.request_id} className="rounded-md border border-[#E5E7EB] p-3" data-testid={`beta-request-${rq.request_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-[#111827]">{rq.full_name}</p>
                    <p className="text-xs text-[#6B7280]">{rq.email} · {rq.phone || "no phone"}</p>
                    <p className="text-xs text-[#6B7280]">{rq.organisation || "independent"} · {rq.designation || "—"} · {rq.city || "—"}</p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full border ${
                    rq.status === "pending" ? "text-[#B58900] border-[#FCC419]"
                      : rq.status === "approved" ? "text-[#2E7D32] border-[#2E7D32]" : "text-[#C92A2A] border-[#C92A2A]"}`}>
                    {rq.status}
                  </span>
                </div>
                <p className="text-xs text-[#111827] mt-2"><b>Qualification:</b> {rq.qualification || "—"}</p>
                <p className="text-xs text-[#111827]"><b>Experience:</b> {rq.experience || "—"}</p>
                <p className="text-[10px] text-[#6B7280] mt-1">
                  Terms v{rq.terms_version} accepted {String(rq.agreed_at || "").slice(0, 16).replace("T", " ")} UTC
                </p>
                {rq.status === "pending" && (
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" onClick={() => approve(rq)} data-testid={`approve-${rq.request_id}`}
                      className="bg-[#002FA7] hover:bg-[#00237C] text-white h-8">
                      <Check className="h-3.5 w-3.5 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => reject(rq)} data-testid={`reject-${rq.request_id}`} className="h-8">
                      <X className="h-3.5 w-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>

        <div className="space-y-4">
          <Section title="Create a User" desc="A temporary password is generated; the user must change it at first sign-in."
            icon={UserPlus} testid="create-user-card">
            <form onSubmit={createUser} className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required data-testid="new-user-name" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required data-testid="new-user-email" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1.5 bg-white" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="technician">Technician</SelectItem>
                    <SelectItem value="pathologist">Pathologist</SelectItem>
                    {isPrimary && <SelectItem value="admin">Administrator</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Access packages</Label>
                <p className="text-[11px] text-[#6B7280] mt-0.5">
                  Pick every package this user has opted for — combinations are allowed. Administrators always
                  get everything.
                </p>
                <div className="mt-2 grid sm:grid-cols-2 gap-2">
                  {packMeta.map((p) => {
                    const on = form.packs.includes(p.key);
                    return (
                      <button type="button" key={p.key} data-testid={`new-user-pack-${p.key}`}
                        onClick={() => setForm({
                          ...form,
                          packs: on ? form.packs.filter((k) => k !== p.key) : [...form.packs, p.key],
                        })}
                        className={`text-left rounded-md border p-2.5 transition-colors ${
                          on ? "border-[#002FA7] bg-[#EFF6FF]" : "border-[#E5E7EB] hover:border-[#002FA7]"}`}>
                        <p className="text-xs font-medium text-[#111827]">{p.label}</p>
                        <p className="text-[11px] text-[#6B7280] mt-0.5">{p.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-end sm:col-span-2">
                <Button type="submit" data-testid="create-user-btn" className="w-full bg-[#002FA7] hover:bg-[#00237C] text-white">
                  Create user
                </Button>
              </div>
            </form>
          </Section>

          {TwoFactorCard}
        </div>

        <Section title="Users" desc="Activate, suspend, change roles, reset passwords or remove accounts."
          icon={KeyRound} testid="admin-users-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-[#F3F4F6]">
                <tr>
                  {["User", "Role", "Lab", "Status", "2FA", "Access", ""].map((h) => (
                    <th key={h} className="text-left font-medium text-xs uppercase tracking-[0.08em] text-[#6B7280] px-3 py-2">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-[#E5E7EB]" data-testid={`admin-user-${u.email}`}>
                    <td className="px-3 py-2">
                      <p className="text-[#111827]">{u.name} {u.is_primary_admin && <span className="text-[10px] uppercase text-[#002FA7]">primary</span>}</p>
                      <p className="text-xs text-[#6B7280]">{u.email} · {u.auth_provider}</p>
                    </td>
                    <td className="px-3 py-2 w-40">
                      <Select value={u.role} onValueChange={(v) => setRole(u, v)}>
                        <SelectTrigger className="h-8 bg-white" data-testid={`admin-role-${u.email}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="technician">Technician</SelectItem>
                          <SelectItem value="pathologist">Pathologist</SelectItem>
                          {(isPrimary || u.role === "admin") && <SelectItem value="admin">Admin</SelectItem>}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2 w-48">
                      {isPrimary && !u.is_primary_admin ? (
                        <select value={u.lab_id || ""} data-testid={`admin-lab-${u.email}`}
                          onChange={(e) => setLab(u, e.target.value)}
                          className="h-8 w-full rounded-md border border-[#E5E7EB] bg-white px-2 text-xs">
                          <option value="">— not linked —</option>
                          {labs.map((l) => <option key={l.lab_id} value={l.lab_id}>{l.name}</option>)}
                        </select>
                      ) : (
                        <span className="text-xs text-[#6B7280]" data-testid={`admin-lab-label-${u.email}`}>
                          {labs.find((l) => l.lab_id === u.lab_id)?.name || "—"}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={() => setStatus(u, u.status === "active" ? "suspended" : "active")}
                        data-testid={`admin-status-${u.email}`}
                        className={`text-[10px] uppercase tracking-[0.1em] px-2 py-1 rounded-full border ${
                          u.status === "active" ? "text-[#2E7D32] border-[#2E7D32]" : "text-[#B58900] border-[#FCC419]"}`}>
                        {u.status || "active"}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-xs text-[#6B7280]">{u.totp_enabled ? "on" : "off"}</td>
                    <td className="px-3 py-2 text-xs text-[#6B7280]">
                      {(u.access_packs || []).join(", ") || (u.role === "admin" ? "all" : "testing")}
                      {(u.access_modules || []).length > 0 && (
                        <span className="block text-[10px] text-[#002FA7]">+ {(u.access_modules || []).join(", ")}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button onClick={() => setAccessTarget(u)} data-testid={`admin-access-${u.email}`}
                        className="text-xs text-[#002FA7] hover:underline mr-3">Access</button>
                      <button onClick={() => resetPassword(u)} data-testid={`admin-reset-${u.email}`}
                        className="text-xs text-[#002FA7] hover:underline mr-3">Reset password</button>
                      {!u.is_primary_admin && u.user_id !== user?.user_id && (
                        <button onClick={() => removeUser(u)} data-testid={`admin-remove-${u.email}`}
                          className="p-1.5 text-[#C92A2A] hover:bg-[#F3F4F6] rounded"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
        <PluginsCard />
        <LoginActivityCard />
      </div>

      {accessTarget && (
        <AccessEditor target={accessTarget} onClose={() => setAccessTarget(null)} onSaved={load} />
      )}
    </div>
  );
}
